import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  FwAvatarModule,
  FwButtonModule,
  FwButtonToggleModule,
  FwCardModule,
  FwContainedInputModule,
  FwIconButtonModule,
  FwIconModule,
  FwLayoutsModule,
  FwMenuModule,
  FwSectionHeadingModule,
  FwSelectMenuModule,
  FwSwitchModule,
  FwTextInputModule,
} from '@flywheel-io/vision';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import { Subscription } from 'rxjs';

import { AdminModule } from 'app/src/admin/admin.module';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { SessionModule } from 'app/src/sessions/session.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

import { Project } from '../../shared/models/project.model';
import { User } from '../../shared/models/user.model';
import { lockReasonLabels } from '../../shared/projects.utils';
import { ProjectsService } from '../projects.service';

@Component({
  host: { 'test-id': 'project-lock-status' },
  selector: 'project-lock-status',
  styleUrls: ['./project-lock-status.component.scss'],
  template: ` @if (project?.locked && project?._id) {
      <span #lockStatus> <mat-icon class="locked xs" test-id="project-lock-icon">lock</mat-icon></span>
      <fw-popover action="mouseenter" position="below" [trigger]="lockStatus">
        <div class="lock-status padding-15">
          <strong class="title">Project is Locked</strong>
          <strong test-id="project-lock-reason">Reason</strong>
          <span>{{ reason }}</span>
          <strong test-id="project-lock-timestamp">Timestamp</strong>
          <span>{{ project.locked.timestamp | date: 'MM/dd/yyyy HH:mm:ss' }}</span>
          <strong>Signed & Locked By</strong>
          <span test-id="project-lock-user">
            <app-avatar [enablePopover]="false" [users]="user"></app-avatar>
            <span>{{ user?.firstname }} {{ user?.lastname }} &lt;{{ user?.email }}&gt;</span>
          </span>
          <a
            class="light-primary-background"
            color="primary"
            href="#/projects/{{ project?._id }}/settings/lock"
            mat-button
            test-id="view-settings-button"
          >
            View Settings to Unlock
          </a>
        </div>
      </fw-popover>
    }
    @if (!project?.locked && project?._id) {
      <span #unlockedStatus>
        <mat-icon class="unlocked xs" test-id="project-lock-icon">lock_open</mat-icon>
      </span>
      <fw-popover action="mouseenter" position="below" [trigger]="unlockedStatus">
        <div class="lock-status padding-15">
          <strong class="title">Project is Unlocked</strong>
          <a
            class="light-primary-background"
            color="primary"
            href="#/projects/{{ project._id }}/settings/lock"
            mat-button
            test-id="view-settings-button"
          >
            View Settings to Lock
          </a>
        </div>
      </fw-popover>
    }`,
  imports: [
    AdminModule,
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwAvatarModule,
    FwButtonModule,
    FwButtonToggleModule,
    FwCardModule,
    FwContainedInputModule,
    FwIconButtonModule,
    FwIconModule,
    FwLayoutsModule,
    FwMenuModule,
    FwSectionHeadingModule,
    FwSelectMenuModule,
    FwSwitchModule,
    FwTextInputModule,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    ScrollingModule,
    SessionModule,
    SharedModule,
    RouterModule,
  ],
})
export class ProjectLockStatusComponent implements OnDestroy {
  private projectsService = inject(ProjectsService);

  @Input() project: Project;

  private subscriptions = {
    users: Subscription.EMPTY,
  };
  private users: Record<string, User> = {};

  constructor() {
    this.subscriptions.users = this.projectsService.getUsers().subscribe((users) => {
      this.users = Object.assign({}, ...users.map((user) => ({ [user._id]: user })));
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get user(): User {
    return this.users[this.project?.locked?.origin.id];
  }

  get reason(): string {
    const key = this.project?.locked?.reason;
    if (!key) {
      return '';
    }
    return lockReasonLabels[camelCase(key)] ?? upperFirst(key.replace(/_/g, ' '));
  }
}
